import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FolderPlus, Loader2, Paperclip, Plus, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ReactMarkdown from 'react-markdown';
import { useLocation } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { isCrossWorkspaceMode, morganApi } from '../../lib/api';
import { MorganActionCard } from './MorganActionCard';
import { DatasetSummaryCard, MatchResults } from './MorganDataCards';
import { MorganAutorunCard } from './MorganAutorunCard';
import { MorganCaseBuilder } from './MorganCaseBuilder';
import { MorganAvatar } from './MorganAvatar';
import { markdownComponents } from './MorganChart';

const ADMIN_ROLES = ['system_admin', 'merchant_admin', 'partner_admin'];

const API = process.env.REACT_APP_BACKEND_URL;
const FILE_LABELS = { returns: 'Returns', exports: 'Exports', declaration_lines: 'Declaration lines', ddp_charges: 'DDP charges',
  eu_returns: 'EU returns', us_7501_entries: 'US 7501 entries', receipts: 'Receipts', destruction_events: 'Destruction events' };
const errText = (e, f) => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : d?.message || f; };

async function streamChat(sid, message, onDelta) {
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('token');
  if (token) headers.Authorization = `Bearer ${token}`;
  const ws = localStorage.getItem('activeWorkspaceId');
  if (ws && !isCrossWorkspaceMode()) headers['X-Workspace-Id'] = ws;
  const res = await fetch(`${API}/api/morgan/sessions/${sid}/chat`, { method: 'POST', credentials: 'include', headers, body: JSON.stringify({ message }) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.detail?.message || 'Morgan is unavailable');
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split('\n\n');
    buf = parts.pop();
    parts.forEach((p) => { if (p.startsWith('data: ')) { const ev = JSON.parse(p.slice(6)); if (ev.delta) onDelta(ev.delta); } });
  }
}

const overlayOpen = () => document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="listbox"], [role="menu"]');

export const MorganPanel = () => {
  const [me, setMe] = useState(null);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [sid, setSidState] = useState(null);
  const sidRef = useRef(null);
  const setSid = useCallback((id) => { sidRef.current = id; setSidState(id); }, []);
  const [sessions, setSessions] = useState([]);
  const [items, setItems] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [fileType, setFileType] = useState('auto');
  useEffect(() => {
    const refresh = () => {
      setFileType('auto'); setSid(null); setItems([]);
      morganApi.me().then((r) => setMe(r.data)).catch(() => {});
      morganApi.sessions().then((r) => setSessions(r.data.sessions || [])).catch(() => setSessions([]));
    };
    const evs = ['workspaceChanged', 'partnerModeChanged', 'overview:global'];
    evs.forEach((e) => window.addEventListener(e, refresh));
    return () => evs.forEach((e) => window.removeEventListener(e, refresh));
  }, [setSid]);

  const fileRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !overlayOpen()) setOpen(false); };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);
  useEffect(() => { morganApi.me().then((r) => setMe(r.data)).catch(() => setMe(null)); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [items]);

  const loadSession = useCallback(async (id) => {
    const r = await morganApi.getSession(id);
    setSid(id);
    setSessions((ss) => (ss.some((x) => x.id === id) || !r.data.session ? ss : [r.data.session, ...ss]));
    setItems([...r.data.messages.map((m) => ({ kind: 'msg', ...m })), ...r.data.actions.map((a) => ({ kind: 'action', ...a, created_at: a.created_at })),
      ...(r.data.datasets || []).map((d) => ({ kind: 'dataset', id: d.id, dataset: d, created_at: d.created_at }))]
      .sort((a, b) => (a.created_at > b.created_at ? 1 : -1)));
  }, [setSid]);

  const ensureSession = useCallback(async (context) => {
    if (sidRef.current) return sidRef.current;
    const r = await morganApi.createSession({ context: context || {} });
    setSid(r.data.id);
    setSessions((s) => [r.data, ...s]);
    return r.data.id;
  }, [setSid]);

  const send = useCallback(async (text, context) => {
    const msg = (text || '').trim();
    if (!msg) return;
    setBusy(true);
    setInput('');
    try {
      const id = await ensureSession(context);
      const replyId = `reply-${Date.now()}`;
      setItems((p) => [...p, { kind: 'msg', id: `u-${Date.now()}`, role: 'user', content: msg }, { kind: 'msg', id: replyId, role: 'assistant', content: '' }]);
      await streamChat(id, msg, (d) => setItems((p) => p.map((it) => (it.id === replyId ? { ...it, content: it.content + d } : it))));
      setSessions((ss) => ss.map((x) => (x.id === id && x.title === 'New Morgan session' ? { ...x, title: msg.slice(0, 60) } : x)));
    } catch (e) {
      toast.error(e.message || 'Morgan is unavailable');
    } finally {
      setBusy(false);
    }
  }, [ensureSession]);

  useEffect(() => {
    const handler = (ev) => {
      setOpen(true);
      morganApi.me().then((r) => setMe(r.data)).catch(() => {});
      morganApi.sessions().then((r) => setSessions(r.data.sessions || [])).catch(() => {});
      if (ev.detail?.prompt) setTimeout(() => send(ev.detail.prompt, ev.detail.context), 50);
      if (ev.detail?.upload) setTimeout(() => fileRef.current?.click(), 150);
      if (ev.detail?.buildCase) setTimeout(() => window.dispatchEvent(new Event('morgan:build-case')), 150);
    };
    window.addEventListener('morgan:open', handler);
    return () => window.removeEventListener('morgan:open', handler);
  }, [send]);

  useEffect(() => {
    const deep = new URLSearchParams(location.search).get('morgan_session');
    if (deep && me?.enabled) {
      setOpen(true);
      morganApi.sessions().then((r) => setSessions(r.data.sessions || [])).catch(() => {});
      loadSession(deep).catch(() => toast.error('That Morgan session is not available'));
    }
  }, [me?.enabled, loadSession, location.search]);

  const openPanel = () => {
    setOpen(true);
    morganApi.me().then((r) => setMe(r.data)).catch(() => {});
    morganApi.sessions().then((r) => setSessions(r.data.sessions || [])).catch(() => {});
  };

  const propose = async (body) => {
    try {
      const id = await ensureSession();
      const r = await morganApi.propose(id, body);
      setItems((p) => [...p, { kind: 'action', ...r.data }]);
    } catch (e) { toast.error(errText(e, 'Morgan cannot do that')); }
  };
  const updateAction = (id, patch) => setItems((p) => p.map((it) => (it.kind === 'action' && it.id === id ? { ...it, ...patch } : it)));
  const confirm = async (a) => {
    try {
      const r = await morganApi.confirm(a.id);
      updateAction(a.id, { status: r.data.status, output: r.data.output });
      const run = a.action_type === 'mrn_override' && r.data.output?.run_matching;
      if (run) {
        setItems((p) => p.map((it) => (it.kind === 'action' && it.id === run.action_id
          ? { ...it, output: { ...it.output, results: run.results, counts: run.counts, next_steps: run.next_steps } } : it)));
      }
      toast.success('Done');
    }
    catch (e) { updateAction(a.id, { status: 'failed', error: e?.response?.data?.detail }); toast.error(errText(e, 'Action failed')); }
  };
  const reject = async (a) => { await morganApi.reject(a.id); updateAction(a.id, { status: 'rejected' }); };
  const prefill = async (datasetId, rowIndex) => {
    try { const id = await ensureSession(); const r = await morganApi.prefill(id, { dataset_id: datasetId, row_index: rowIndex }); await propose({ ...r.data, summary: 'Create RGR case from matched row' }); }
    catch (e) { toast.error(errText(e, 'Could not pre-fill')); }
  };

  const autorun = async (datasetId, sessionId) => {
    setBusy(true);
    try {
      const id = sessionId || await ensureSession();
      const r = await morganApi.autonomousRun(id, { dataset_id: datasetId });
      toast.success(`Autonomous run (Level ${r.data.level}) ${r.data.status.replace(/_/g, ' ')}`);
      await loadSession(id);
    } catch (e) { toast.error(errText(e, 'Autonomous run failed')); } finally { setBusy(false); }
  };
  const revert = async (overrideId) => {
    const reason = window.prompt('Reason for reverting this override (min 5 characters)');
    if (!reason || reason.trim().length < 5) return;
    try { await morganApi.revertOverride(overrideId, reason.trim()); toast.success('Override reverted'); if (sidRef.current) await loadSession(sidRef.current); }
    catch (e) { toast.error(errText(e, 'Revert failed')); }
  };

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const id = await ensureSession();
      const r = await morganApi.upload(id, file, fileType === 'auto' ? null : fileType);
      setItems((p) => [...p, { kind: 'dataset', id: r.data.dataset.id, dataset: r.data.dataset }]);
      setSessions((ss) => ss.map((x) => (x.id === id && x.title === 'New Morgan session' ? { ...x, title: `Upload: ${r.data.dataset.filename}`.slice(0, 60) } : x)));
      if (r.data.warning) setItems((p) => [...p, { kind: 'msg', id: `warn-${Date.now()}`, role: 'assistant', content: r.data.warning }]);
      if (me?.autonomous_execution_active) { await autorun(r.data.dataset.id, id); return; }
      for (const s of r.data.suggested_actions || []) await propose(s);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (['file_type_not_enabled', 'no_lanes_enabled', 'invalid_file'].includes(d?.code)) {
        setItems((p) => [...p, { kind: 'msg', id: `rej-${Date.now()}`, role: 'assistant', content: d.message }]);
      } else toast.error(errText(e, 'Upload failed'));
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  if (!me?.enabled) return null;
  const types = me.supported_file_types || [];
  const canExecute = me.can_execute;
  return (
    <>
      {!open && (
        <button type="button" onClick={openPanel} data-testid="morgan-launcher"
                className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 rounded-full border border-[var(--color-nav-border)] bg-[var(--color-nav-bg)] py-1.5 pl-1.5 pr-5 text-sm font-semibold text-white shadow-[0_10px_30px_-8px_rgba(11,42,91,0.6)] transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-[var(--color-nav-hover-bg)]">
          <MorganAvatar size={32} testId="morgan-launcher-icon" /> Ask Morgan
        </button>
      )}
      {open && (
        <aside className="morgan-panel-in fixed bottom-0 right-0 top-0 z-50 flex w-[440px] max-w-full flex-col border-l border-slate-200 bg-[#F6F8FB] shadow-[-12px_0_40px_-12px_rgba(11,42,91,0.25)]" data-testid="morgan-panel">
          <header className="flex items-center gap-3 bg-[var(--color-nav-bg)] px-4 py-3 text-white">
            <MorganAvatar size={36} className="ring-2 ring-white/15" testId="morgan-header-icon" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 font-semibold leading-tight">Morgan <span className="h-2 w-2 rounded-full bg-emerald-400" aria-hidden="true" /></div>
              <div className="truncate text-[11px] text-[var(--color-nav-section)]" data-testid="morgan-tier">
                {me.tier === 'pro' ? `Pro · Level ${me.pro_level}${me.autonomous_execution_active ? ' · autonomy on' : me.pro_level > 1 ? ' (autonomy off: suggest only)' : ''}` : 'Standard'} · {me.mode.replace('_', ' ')}
              </div>
            </div>
            <Button size="icon" variant="ghost" className="rounded-full text-white hover:bg-white/10" onClick={() => { setSid(null); setItems([]); }} title="New session" aria-label="New session" data-testid="morgan-new-session"><Plus className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" className="rounded-full text-white hover:bg-white/10" onClick={() => setOpen(false)} title="Close" aria-label="Close Morgan" data-testid="morgan-close"><X className="w-4 h-4" /></Button>
          </header>
          {sessions.length > 0 && (
            <div className="border-b border-slate-200 bg-white px-3 py-2">
              <Select value={sid || ''} onValueChange={loadSession}>
                <SelectTrigger className="h-10 text-xs" data-testid="morgan-session-select"><SelectValue placeholder="Previous sessions" /></SelectTrigger>
                <SelectContent>{sessions.map((s) => <SelectItem key={s.id} value={s.id} className="text-xs">{s.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {me.mode !== 'full' && (
            <div className="bg-amber-50 px-4 py-2 text-xs text-amber-900" data-testid="morgan-mode-banner">
              {me.mode === 'read_only' ? 'Read-only role: Morgan explains and summarises, but cannot execute actions.' : 'Intake role: Morgan gives intake and evidence guidance only.'}
            </div>
          )}
          <div className="flex-1 space-y-4 overflow-y-auto px-4 py-5" data-testid="morgan-timeline">
            <MorganCaseBuilder open={open} canBuild={canExecute} />
            {items.length === 0 && (
              <div className="morgan-in flex items-start gap-2.5" data-testid="morgan-empty">
                <MorganAvatar size={28} className="mt-0.5" />
                <div className="rounded-2xl rounded-tl-md border border-slate-200 bg-white px-3.5 py-2.5 text-sm leading-relaxed text-slate-700 shadow-sm" data-testid="morgan-greeting">
                  {me.greeting || "Hi, I'm Morgan. Ask me about a case or a gate failure."}
                </div>
              </div>
            )}
            {items.map((it) => <TimelineItem key={`${it.kind}-${it.id}`} it={it} canExecute={canExecute} onConfirm={confirm} onReject={reject} propose={propose} prefill={prefill}
                                         onAutorun={me.autonomous_execution_active ? autorun : null} onRevert={ADMIN_ROLES.includes(me.role) ? revert : null} />)}
            <div ref={endRef} />
          </div>
          <footer className="space-y-2 border-t border-slate-200 bg-white p-3">
            <div className="flex items-center gap-2">
              {types.length === 0 ? (
                <span className="text-xs text-slate-500" data-testid="morgan-no-uploads">No lanes are switched on here, so uploads are off.</span>
              ) : (<>
              <Select value={types.includes(fileType) ? fileType : 'auto'} onValueChange={setFileType}>
                <SelectTrigger className="h-10 w-[120px] text-xs" data-testid="morgan-file-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-detect</SelectItem>
                  {types.map((t) => <SelectItem key={t} value={t} data-testid={`morgan-file-type-${t}`}>{FILE_LABELS[t] || t.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="morgan-file-input" />
              <Button size="sm" variant="outline" className="h-10" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="morgan-upload-btn">
                <Paperclip className="w-3.5 h-3.5 mr-1" /> CSV
              </Button>
              </>)}
              {canExecute && (
                <Button size="sm" variant="outline" className="h-10" onClick={() => window.dispatchEvent(new Event('morgan:build-case'))} data-testid="morgan-build-case-btn">
                  <FolderPlus className="w-3.5 h-3.5 mr-1" /> Build a case
                </Button>
              )}
              <Badge variant="outline" className="ml-auto text-[10px] font-normal hidden">morgan-v1</Badge>
            </div>
            <div className="flex gap-2">
              <Textarea rows={2} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask Morgan…" className="resize-none rounded-xl border-slate-300 bg-slate-50 text-sm focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }} data-testid="morgan-input" />
              <Button disabled={busy || !input.trim()} onClick={() => send(input)} aria-label="Send message" data-testid="morgan-send-btn"
                      className="h-auto self-stretch rounded-xl bg-[var(--color-primary)] px-3.5 hover:bg-[var(--color-primary-dark)]">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </footer>
        </aside>
      )}
    </>
  );
};

const TimelineItem = ({ it, canExecute, onConfirm, onReject, propose, prefill, onAutorun, onRevert }) => {
  if (it.kind === 'dataset') return <DatasetSummaryCard dataset={it.dataset} onAutorun={onAutorun ? (dsId) => onAutorun(dsId) : null} />;
  if (it.kind === 'action' && it.action_type === 'autonomous_run') return <MorganAutorunCard action={it} />;
  if (it.kind === 'action') {
    return (
      <div className="space-y-2">
        {!it.autonomous && <MorganActionCard action={it} canExecute={canExecute} onConfirm={onConfirm} onReject={onReject} />}
        {it.action_type === 'run_matching' && it.status === 'executed' && (
          <MatchResults action={it} canExecute={canExecute} prefill={prefill} onPrefill={prefill} onRevert={onRevert}
                        onCreateCases={(datasetId) => propose({ action_type: 'create_reexport_cases', params: { dataset_id: datasetId }, summary: 'Create UK Import re-export cases from matched rows' })}
                        onOverride={(params) => propose({ action_type: 'mrn_override', params, summary: `Manual MRN override → ${params.mrn}` })} />
        )}
      </div>
    );
  }
  const mine = it.role === 'user';
  return (
    <div className={`morgan-in flex items-start gap-2.5 ${mine ? 'justify-end' : 'justify-start'}`}>
      {!mine && <MorganAvatar size={28} className="mt-0.5" testId="morgan-msg-avatar" />}
      <div className={`min-w-0 text-sm leading-relaxed ${mine
        ? 'max-w-[80%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-[var(--color-primary)] px-3.5 py-2 text-white shadow-sm'
        : 'morgan-md max-w-[calc(100%-2.5rem)] flex-1 rounded-2xl rounded-tl-md border border-slate-200 bg-white px-3.5 py-2.5 text-slate-800 shadow-sm'}`}
           data-testid={mine ? 'morgan-msg-user' : 'morgan-msg-assistant'}>
        {!it.content ? <span className="flex gap-1 py-1" data-testid="morgan-typing">{[0, 1, 2].map((i) => <span key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: `${i * 120}ms` }} />)}</span>
          : mine ? it.content : <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{it.content}</ReactMarkdown>}
      </div>
    </div>
  );
};
